"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { FieldConfig, PublicFormConfig } from "@/lib/forms-client";
import {
  collectPageContext,
  isFieldVisible,
  maskCep,
  maskCpfCnpj,
  maskPhone,
  validateField,
} from "@/lib/forms-client";
import { CaptchaWidget } from "@/components/auth/CaptchaWidget";

/** Tipos que podem dividir uma linha (width="half"). */
const PAIRABLE = [
  "TEXT", "EMAIL", "TEL", "NUMBER", "DATE", "SELECT",
  "TIME", "URL", "CPF_CNPJ", "CEP",
];

/** Validade do rascunho salvo em localStorage. */
const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;

type FileState = { status: "uploading" | "done" | "error"; name?: string; error?: string };

interface Props {
  config: PublicFormConfig;
  wrapperClass?: string;
  heading?: string;
  subheading?: string;
  variant?: "default" | "footer";
  onSuccess?: () => void;
}

function initialValues(fields: FieldConfig[]): Record<string, unknown> {
  const init: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === "CHECKBOX" || f.type === "CONSENT") init[f.name] = false;
    else if (f.type === "CHECKBOX_GROUP") init[f.name] = [];
    else init[f.name] = f.defaultValue ?? "";
  }
  return init;
}

export function DynamicFormClient({
  config,
  wrapperClass = "contact-form",
  heading,
  subheading,
  variant = "default",
  onSuccess,
}: Props) {
  const footer = variant === "footer";
  const draftKey = `ge_form_draft_${config.slug}`;

  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, FileState>>({});
  const [cepLoading, setCepLoading] = useState<Record<string, boolean>>({});
  const [captchaReady, setCaptchaReady] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>(() => initialValues(config.fields));
  const formRef = useRef<HTMLFormElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);

  // Beacon de visualização (taxa de conversão).
  useEffect(() => {
    fetch(`/api/forms/${config.slug}/view`, { method: "POST", keepalive: true }).catch(() => {});
  }, [config.slug]);

  // Restaura o rascunho (não no rodapé — newsletter é trivial).
  useEffect(() => {
    if (footer) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw) as { savedAt?: number; values?: Record<string, unknown> };
      if (d.savedAt && d.values && Date.now() - d.savedAt < DRAFT_TTL) {
        // Sincroniza o estado com o rascunho persistido — leitura externa
        // (localStorage), intencional só no mount.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setValues((v) => ({ ...v, ...d.values }));
        setDraftRestored(true);
      } else {
        localStorage.removeItem(draftKey);
      }
    } catch {
      /* ignora */
    }
  }, [draftKey, footer]);

  // Salva o rascunho (debounce).
  useEffect(() => {
    if (footer || status === "sent") return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ savedAt: Date.now(), values }));
      } catch {
        /* localStorage cheio/indisponível */
      }
    }, 600);
    return () => clearTimeout(t);
  }, [values, draftKey, footer, status]);

  function clearDraft() {
    try {
      localStorage.removeItem(draftKey);
    } catch {
      /* ignora */
    }
  }

  function discardDraft() {
    setValues(initialValues(config.fields));
    setErrors({});
    setDraftRestored(false);
    clearDraft();
  }

  /** Atualiza um valor e limpa o erro do campo se ele passou a ser válido. */
  function setVal(name: string, v: unknown) {
    setValues((p) => ({ ...p, [name]: v }));
    setErrors((e) => {
      if (!e[name]) return e;
      const f = config.fields.find((x) => x.name === name);
      if (f && !validateField(f, v)) {
        const n = { ...e };
        delete n[name];
        return n;
      }
      return e;
    });
  }

  /** Valida um campo ao perder o foco. */
  function handleBlur(f: FieldConfig) {
    const err = validateField(f, values[f.name]);
    setErrors((e) => {
      const n = { ...e };
      if (err) n[f.name] = err;
      else delete n[f.name];
      return n;
    });
    if (f.type === "CEP") void autofillCep(f);
  }

  /** Consulta o CEP e preenche os campos de endereço mapeados. */
  async function autofillCep(f: FieldConfig) {
    const digits = String(values[f.name] ?? "").replace(/\D/g, "");
    if (digits.length !== 8 || !f.cepAutofill) return;
    setCepLoading((s) => ({ ...s, [f.name]: true }));
    try {
      const r = await fetch(`/api/cep/${digits}`).then((x) => x.json());
      if (r.ok) {
        const a = f.cepAutofill;
        setValues((v) => {
          const n = { ...v };
          if (a.street && r.street) n[a.street] = r.street;
          if (a.neighborhood && r.neighborhood) n[a.neighborhood] = r.neighborhood;
          if (a.city && r.city) n[a.city] = r.city;
          if (a.state && r.state) n[a.state] = r.state;
          return n;
        });
      }
    } catch {
      /* CEP indisponível — o usuário preenche à mão */
    }
    setCepLoading((s) => ({ ...s, [f.name]: false }));
  }

  async function uploadFile(field: FieldConfig, list: FileList | null) {
    const file = list?.[0];
    if (!file) return;
    setFiles((s) => ({ ...s, [field.name]: { status: "uploading" } }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch("/api/forms/upload", { method: "POST", body: fd });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) {
        setVal(field.name, j.url);
        setFiles((s) => ({ ...s, [field.name]: { status: "done", name: j.name } }));
      } else {
        setVal(field.name, "");
        setFiles((s) => ({ ...s, [field.name]: { status: "error", error: j.error || "Falha no envio" } }));
      }
    } catch {
      setVal(field.name, "");
      setFiles((s) => ({ ...s, [field.name]: { status: "error", error: "Falha de conexão" } }));
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;

    // Validação inline de todos os campos visíveis antes de enviar.
    const found: Record<string, string> = {};
    for (const f of config.fields) {
      if (!isFieldVisible(f, values)) continue;
      const err = validateField(f, values[f.name]);
      if (err) found[f.name] = err;
    }
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setStatus("error");
      setErrorMsg("Revise os campos destacados.");
      const first = config.fields.find((f) => found[f.name]);
      if (first) document.getElementById(`${config.slug}-${first.name}`)?.focus();
      return;
    }

    setStatus("sending");
    setErrorMsg("");
    setErrors({});

    const data: Record<string, unknown> = {};
    for (const f of config.fields) {
      if (isFieldVisible(f, values)) data[f.name] = values[f.name];
    }

    // Tokens do captcha — escritos pelo CaptchaWidget como inputs ocultos.
    const fd = formRef.current ? new FormData(formRef.current) : null;
    const hpVal = honeypotRef.current?.value ?? "";
    try {
      const res = await fetch(`/api/forms/${config.slug}/submit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          data,
          context: collectPageContext(),
          recaptchaToken: fd?.get("recaptchaToken") ?? "",
          powChallenge: fd?.get("powChallenge") ?? "",
          powNonce: fd?.get("powNonce") ?? "",
          website: hpVal,
          _ge_hp_val: hpVal,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.ok) {
        clearDraft();
        if (typeof window !== "undefined") {
          const w = window as any;
          w.dataLayer = w.dataLayer || [];
          w.dataLayer.push({
            event: "generate_lead",
            form_id: config.slug,
            form_name: config.name,
          });
        }
        const targetRedirect =
          typeof json.redirect === "string" && json.redirect
            ? json.redirect
            : config.submitBehavior === "REDIRECT"
            ? "/mensagem-enviada"
            : null;

        if (targetRedirect) {
          window.location.assign(targetRedirect);
          return;
        }
        setStatus("sent");
        onSuccess?.();
      } else {
        setStatus("error");
        if (json?.issues) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(json.issues as Record<string, string[]>)) {
            if (Array.isArray(v) && v[0]) flat[k] = v[0];
          }
          setErrors(flat);
        }
        setErrorMsg(json?.error || "Não foi possível enviar. Tente novamente ou use o WhatsApp.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Falha de conexão. Tente novamente ou use o WhatsApp.");
    }
  }

  if (status === "sent") {
    if (footer) {
      return (
        <p className="foot__news-ok" role="status">
          ✓ {config.successMessage || "Inscrição confirmada."}
        </p>
      );
    }
    return (
      <div className={wrapperClass} role="status" style={{ textAlign: "center" }}>
        <div className="lp-form__success-mark">✓</div>
        <h3 style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>Enviado</h3>
        <p style={{ color: "var(--ink-3)", fontSize: 15 }}>
          {config.successMessage || "Recebemos seu contato — em breve retornamos."}
        </p>
      </div>
    );
  }

  // Campos visíveis dado o estado atual.
  const visibleFields = config.fields.filter((f) => isFieldVisible(f, values));

  // Agrupa campos "half" consecutivos em linhas de dois.
  const rows: FieldConfig[][] = [];
  for (let i = 0; i < visibleFields.length; ) {
    const f = visibleFields[i];
    const next = visibleFields[i + 1];
    if (
      f.width === "half" && PAIRABLE.includes(f.type) &&
      next && next.width === "half" && PAIRABLE.includes(next.type)
    ) {
      rows.push([f, next]);
      i += 2;
    } else {
      rows.push([f]);
      i += 1;
    }
  }

  function fieldError(name: string) {
    return errors[name] ? <p className="field__err">{errors[name]}</p> : null;
  }

  function renderControl(f: FieldConfig) {
    const id = `${config.slug}-${f.name}`;
    const v = values[f.name];
    const common = {
      id,
      name: f.name,
      required: f.required,
      ...(errors[f.name] ? { "aria-invalid": true as const } : {}),
      ...(footer ? { "aria-label": f.label } : {}),
    };
    const blur = () => handleBlur(f);
    switch (f.type) {
      case "TEXTAREA":
        return (
          <textarea {...common} rows={5} placeholder={f.placeholder} maxLength={f.maxLength}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />
        );
      case "SELECT":
        return (
          <select {...common} value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur}>
            <option value="">{f.placeholder || "Selecione…"}</option>
            {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case "RADIO":
        return (
          <div className="field__radios">
            {(f.options ?? []).map((o) => (
              <label key={o.value} className="field--check">
                <input type="radio" name={f.name} value={o.value} required={f.required}
                  checked={v === o.value} onChange={() => setVal(f.name, o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        );
      case "CHECKBOX_GROUP": {
        const arr = Array.isArray(v) ? (v as string[]) : [];
        return (
          <div className="field__radios">
            {(f.options ?? []).map((o) => (
              <label key={o.value} className="field--check">
                <input type="checkbox" name={f.name} value={o.value} checked={arr.includes(o.value)}
                  onChange={(e) => setVal(f.name, e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        );
      }
      case "NUMBER":
        return (
          <input {...common} type="number" placeholder={f.placeholder} min={f.min} max={f.max}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />
        );
      case "DATE":
        return <input {...common} type="date" value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />;
      case "TIME":
        return <input {...common} type="time" value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />;
      case "URL":
        return (
          <input {...common} type="url" placeholder={f.placeholder || "https://"}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />
        );
      case "CEP":
        return (
          <input {...common} type="text" inputMode="numeric" placeholder={f.placeholder || "00000-000"}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, maskCep(e.target.value))} onBlur={blur} />
        );
      case "CPF_CNPJ":
        return (
          <input {...common} type="text" inputMode="numeric" placeholder={f.placeholder || "CPF ou CNPJ"}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, maskCpfCnpj(e.target.value))} onBlur={blur} />
        );
      case "EMAIL":
        return (
          <input {...common} type="email" placeholder={f.placeholder}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />
        );
      case "TEL":
        return (
          <input {...common} type="tel" inputMode="tel" placeholder={f.placeholder || "(00) 0 0000-0000"}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, maskPhone(e.target.value))} onBlur={blur} />
        );
      case "FILE": {
        const fs = files[f.name];
        return (
          <>
            <input id={id} type="file" accept={f.accept} required={f.required && !v}
              onChange={(e) => uploadFile(f, e.target.files)} />
            {fs?.status === "uploading" ? <p className="field__help">Enviando arquivo…</p> : null}
            {fs?.status === "done" ? <p className="field__help">✓ {fs.name}</p> : null}
            {fs?.status === "error" ? <p className="field__err">{fs.error}</p> : null}
          </>
        );
      }
      case "RATING": {
        const scale = Math.max(2, Math.min(10, f.max ?? 5));
        const current = Number(v ?? 0);
        return (
          <div className="field__rating" role="radiogroup" aria-label={f.label}>
            {Array.from({ length: scale }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={`field__rating-btn${n <= current ? " is-on" : ""}`}
                aria-pressed={n === current} onClick={() => setVal(f.name, n)}>
                {n}
              </button>
            ))}
          </div>
        );
      }
      default:
        return (
          <input {...common} type="text" placeholder={f.placeholder} maxLength={f.maxLength}
            minLength={f.minLength} pattern={f.pattern}
            value={String(v ?? "")} onChange={(e) => setVal(f.name, e.target.value)} onBlur={blur} />
        );
    }
  }

  function renderField(f: FieldConfig) {
    if (f.type === "SECTION") {
      return (
        <div className="form__section" key={f.name}>
          <h4>{f.label}</h4>
          {f.helpText ? <p>{f.helpText}</p> : null}
        </div>
      );
    }
    if (f.type === "HIDDEN") {
      return <input key={f.name} type="hidden" name={f.name} value={String(values[f.name] ?? "")} />;
    }
    if (f.type === "CONSENT") {
      return (
        <label key={f.name} className="field--check">
          <input type="checkbox" name={f.name} required={f.required}
            checked={values[f.name] === true} onChange={(e) => setVal(f.name, e.target.checked)} />
          <span>
            {config.consentText || f.label}{" "}
            Veja a{" "}
            <Link href="/privacidade" style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Política de Privacidade</Link>,{" "}
            <Link href="/lgpd" style={{ color: "var(--c-accent)", textDecoration: "underline" }}>LGPD</Link>{" "}
            e{" "}
            <Link href="/cookies" style={{ color: "var(--c-accent)", textDecoration: "underline" }}>Cookies</Link>.
          </span>
          {fieldError(f.name)}
        </label>
      );
    }
    if (f.type === "CHECKBOX") {
      return (
        <label key={f.name} className="field--check">
          <input type="checkbox" name={f.name} required={f.required}
            checked={values[f.name] === true} onChange={(e) => setVal(f.name, e.target.checked)} />
          <span>{f.label}</span>
          {fieldError(f.name)}
        </label>
      );
    }
    if (footer) {
      return <div key={f.name}>{renderControl(f)}{fieldError(f.name)}</div>;
    }
    return (
      <div className="field" key={f.name}>
        <label htmlFor={`${config.slug}-${f.name}`}>
          {f.label}
          {f.required ? <span className="field__req" aria-hidden> *</span> : null}
        </label>
        {renderControl(f)}
        {cepLoading[f.name] ? <p className="field__help">Buscando endereço…</p> : null}
        {f.helpText ? <p className="field__help">{f.helpText}</p> : null}
        {fieldError(f.name)}
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={footer ? "form" : `${wrapperClass} form`}
      action={`/api/forms/${config.slug}/submit`}
      method="post"
      onSubmit={onSubmit}
      noValidate
    >
      {heading && !footer ? (
        <div className="lp-form__head">
          <h3>{heading}</h3>
          {subheading ? <p>{subheading}</p> : null}
        </div>
      ) : null}

      {draftRestored && !footer ? (
        <p className="form__draft" role="status">
          Rascunho restaurado.{" "}
          <button type="button" className="form__draft-clear" onClick={discardDraft}>
            Limpar e recomeçar
          </button>
        </p>
      ) : null}

      {config.honeypotEnabled ? (
        <input
          ref={honeypotRef}
          type="text"
          name="_ge_hp_val"
          tabIndex={-1}
          autoComplete="new-password"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        />
      ) : null}

      {rows.map((row, idx) =>
        row.length === 2 ? (
          <div className="field--row" key={idx}>
            {renderField(row[0])}
            {renderField(row[1])}
          </div>
        ) : (
          renderField(row[0])
        ),
      )}

      {config.captchaEnabled ? (
        <CaptchaWidget flow="form" variant="dark" onReady={setCaptchaReady} />
      ) : null}

      {status === "error" && errorMsg ? (
        <p className={footer ? "foot__news-err" : "field__err"} role="alert">{errorMsg}</p>
      ) : null}

      {footer ? (
        <button className="btn btn--ghost" type="submit"
          style={{ width: "100%", justifyContent: "center" }}
          disabled={status === "sending" || (config.captchaEnabled && !captchaReady)}>
          {status === "sending" ? "Enviando…" : config.submitLabel}
        </button>
      ) : (
        <button className="btn btn--primary btn--lg" type="submit"
          style={{ alignSelf: "flex-start" }}
          disabled={status === "sending" || (config.captchaEnabled && !captchaReady)}>
          {status === "sending" ? "Enviando…" : config.submitLabel} <span className="arrow">→</span>
        </button>
      )}
    </form>
  );
}
